import { LoadingOutlined } from "@ant-design/icons";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { FC, useEffect, useState } from "react";
import { Button, Col, Input, Row, Select } from "antd";

import { ICountry, IJobGrade, IModal, IShortTermPlan, IPlanType } from "@types";
import { showSuccessPopup } from "@utils";
import {
  ErrorServices,
  useCreateShortTermPlanMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchCompanyCountriesQuery,
  useFetchShortTermPlanTypesQuery,
  useUpdateShortTermPlanMutation,
} from "@services";
import { useFormValidation, useTypedSelector } from "@hooks";
import CustomModal from "@/components/customModel";
import { ALL_GRADES_VALUE, ALL_VALUE } from "@/utils/constant";

const { Option } = Select;

interface IFormValues {
  name: string;
  plan_type_id: null | number;
  grades: string[];
  country_ids?: number[];
}

interface IAddShortTermPlan extends IModal {
  selectedShortTermPlan: IShortTermPlan | null;
  setSelectedShortTermPlan: React.Dispatch<
    React.SetStateAction<IShortTermPlan | null>
  >;
  isEdit?: boolean;
}

const AddShortTermPlan: FC<IAddShortTermPlan> = ({
  isVisible,
  setIsVisible,
  selectedShortTermPlan,
  setSelectedShortTermPlan,
  isEdit,
}) => {
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const [isGlobal, setIsGlobal] = useState(false);
  const [gradeCountries, setGradeCountries] = useState<ICountry[]>([]);

  const [isAllGrades, setIsAllGrades] = useState(false);

  const { data: planTypesData, isLoading: isFetchingTypes } =
    useFetchShortTermPlanTypesQuery(null);
  const { data: planTypes } = planTypesData || {};
  const { data: jobGradesData, isLoading: isFetchingGrades } =
    useFetchAllCompanyJobGradesQuery({ company_id });
  const { data: jobGrades } = jobGradesData || {};
  const { data: countriesData, isLoading: isFetchingCountries } =
    useFetchCompanyCountriesQuery({
      company_id,
    });
  const { data: countries } = countriesData || {};
  const [createShortTermPlan, { isLoading: isCreating }] =
    useCreateShortTermPlanMutation();
  const [updateShortTermPlan, { isLoading: isUpdating }] =
    useUpdateShortTermPlanMutation();
  const [shortTermPlan, setShortTermPlan] = useState<IFormValues>({
    name: "",
    plan_type_id: null,
    country_ids: [],
    grades: [],
  });
  const handleGradeChangeForEdit = (selectedGrades: string[]) => {
    const selectedJobGrades = jobGrades?.filter(jg =>
      selectedGrades?.includes(jg.grade)
    );
    const mergedCountries =
      selectedJobGrades?.flatMap(jg => jg.countries || []) || [];
    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
    );
    setGradeCountries(uniqueCountries);

    setShortTermPlan(prev => ({
      ...prev,
      grades: selectedGrades,
      // country_id: null,
    }));
  };

  const validation = useFormValidation(shortTermPlan, v => ({
    name: !v.name,
    plan_type: !v.plan_type_id,
    grades: !isAllGrades && (!v.grades || v.grades.length === 0),
    countries: !v.country_ids || v.country_ids.length === 0,
  }));

  useEffect(() => {
    if (selectedShortTermPlan && isEdit) {
      const { grades, is_all_grade, is_global, name, countries, type } =
        selectedShortTermPlan;
      const normalizedCountries =
        countries?.map((c: any) => ({
          id: c.id,
          name: c?.country, // 🔥 KEY FIX
        })) || [];
      setShortTermPlan({
        grades,
        name,
        plan_type_id: type?.id,
      });
      setIsGlobal(is_global);
      setIsAllGrades(is_all_grade);

      handleGradeChangeForEdit(selectedShortTermPlan?.grades);
      if (normalizedCountries?.length > 0) {
        const countryIds = normalizedCountries?.map(c => c?.id);
        handleSelectChange("country_ids", countryIds);
      }
    } else {
      setShortTermPlan({
        name: "",
        plan_type_id: null,
        country_ids: [],
        grades: [],
      });
    }
    // return () => {
    //   setSelectedShortTermPlan(null);
    // };
  }, [selectedShortTermPlan, jobGradesData?.data]);
  const handleGradeChange = (selectedGrades: string[]) => {
    // Check if user selected "All Grades"
    if (selectedGrades.includes(ALL_GRADES_VALUE)) {
      const allGrades = [
        ...new Set(jobGrades?.map(jg => jg.grade).filter(Boolean)),
      ];

      // Get all countries for all grades
      const allCountries = jobGrades
        ?.flatMap(jg => jg.countries || [])
        .filter(
          (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
        );

      setGradeCountries(allCountries);

      setShortTermPlan(prev => ({
        ...prev,
        grades: allGrades,
        country_ids: [],
      }));
      return;
    }

    // Normal selection
    const selectedJobGrades = jobGrades?.filter(jg =>
      selectedGrades.includes(jg.grade)
    );

    // Merge countries for selected grades
    const mergedCountries =
      selectedJobGrades?.flatMap(jg => jg.countries || []) || [];

    // Remove duplicates
    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
    );

    // Update state
    setGradeCountries(uniqueCountries);
    setShortTermPlan(prev => ({
      ...prev,
      grades: selectedGrades,
      country_ids: [], // Reset countries when grades change
    }));
  };

  const handleSelectChange = (name: string, value: any) => {
    setShortTermPlan(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSubmit = async () => {
    try {
      const { grades, country_ids } = shortTermPlan;
      const body = {
        ...shortTermPlan,
        grades: isAllGrades ? [] : grades,
        country_ids: country_ids,
      };
      if (selectedShortTermPlan && isEdit) {
        await editShortTermPlan(body);
      } else {
        await addShortTermPlan(body);
      }
      setIsVisible(false);
      showSuccessPopup({
        title: `Short Term Incentive Plan ${isEdit ? "Updated!" : "Created!"}`,
        desc: `You have successfully ${
          isEdit ? "updated the" : "created new"
        } short term plan.`,
        role: "client",
      });
    } catch (error) {
      ErrorServices(error);
    }
  };

  const addShortTermPlan = async (body: IFormValues) =>
    await createShortTermPlan({ company_id, body }).unwrap();

  const editShortTermPlan = async (body: IFormValues) =>
    await updateShortTermPlan({
      company_id,
      id: selectedShortTermPlan?.id,
      body,
    }).unwrap();
  return (
    <CustomModal
      width="w-6/12"
      footer={
        <>
          <button
            className="bg-themeGrey text-themeBlack font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            Cancel
          </button>
          <button
            className={`bg-themeBlue text-white font-medium text-lg py-2 px-14 rounded-md ${
              !validation.isValid || isCreating || isUpdating
                ? "opacity-50"
                : ""
            }`}
            disabled={!validation.isValid || isCreating || isUpdating}
            onClick={handleSubmit}
          >
            {isCreating || isUpdating ? (
              <LoadingOutlined className="spinner" />
            ) : isEdit ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </>
      }
      title={`${isEdit ? "Update" : "Create"} a STI Plan`}
      isVisible={isVisible}
    >
      <>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Plan Name
            </label>
            <Input
              className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
              value={shortTermPlan?.name}
              onBlur={() => validation.touch("name")}
              onChange={e =>
                setShortTermPlan(prev => ({ ...prev, name: e?.target.value }))
              }
              size="large"
              placeholder="Enter plan name here..."
            />
          </Col>
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Plan Type
            </label>
            <Select
              className="custom-select"
              value={shortTermPlan?.plan_type_id || undefined}
              loading={isFetchingTypes}
              size="large"
              showSearch={false}
              showArrow={true}
              placeholder="Select plan type from here..."
              onBlur={() => validation.touch("plan_type")}
              onChange={plan_type_id =>
                setShortTermPlan(prev => ({
                  ...prev,
                  plan_type_id,
                }))
              }
            >
              {planTypes?.map(({ id, name }: IPlanType) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4">
              Grades
            </label>

            <Select
              className="custom-select"
              disabled={isAllGrades}
              loading={isFetchingGrades}
              mode="multiple"
              showArrow
              size="large"
              placeholder="Select grades from here..."
              value={shortTermPlan?.grades}
              onBlur={() => validation.touch("grades")}
              onChange={handleGradeChange}
            >
              {/* All Grades option (only show if not all selected) */}
              {![
                ...new Set(jobGrades?.map(jgd => jgd?.grade).filter(Boolean)),
              ].every(g => shortTermPlan?.grades?.includes(g)) && (
                <Option key={ALL_GRADES_VALUE} value={ALL_GRADES_VALUE}>
                  All Grades
                </Option>
              )}

              {[
                ...new Set(jobGrades?.map(jgd => jgd?.grade).filter(Boolean)),
              ].map(grade => (
                <Option key={grade} value={grade}>
                  {grade}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Countries
            </label>
            <Select
              className="custom-select"
              disabled={isGlobal || shortTermPlan?.grades?.length === 0}
              loading={isFetchingCountries}
              mode="multiple"
              size="large"
              showSearch
              showArrow
              optionFilterProp="children"
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={shortTermPlan?.country_ids}
              onBlur={() => validation.touch("countries")}
              onChange={(country_ids: any[]) => {
                if (country_ids.includes(ALL_VALUE)) {
                  setShortTermPlan(prev => ({
                    ...prev,
                    country_ids: gradeCountries?.map(c => c.id),
                  }));
                } else {
                  setShortTermPlan(prev => ({
                    ...prev,
                    country_ids,
                  }));
                }
              }}
              placeholder="Select country from here..."
            >
              {!gradeCountries?.every(c =>
                shortTermPlan?.country_ids?.includes(c.id)
              ) && (
                <Option key={ALL_VALUE} value={ALL_VALUE}>
                  All Countries
                </Option>
              )}
              {gradeCountries?.map(({ id, name }: ICountry) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </>
    </CustomModal>
  );
};

export default AddShortTermPlan;
